import React, { useCallback, useMemo } from 'react';
import { Form, reduxForm } from 'redux-form';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';

import { Link, TextInput, ThemeButton, Translation } from '../../common';

import { LOGIN } from '../../../constants/routes';

import { required } from '../../../utils/validation';

const useStyles = makeStyles(theme => ({
  form: {
    padding: '32px'
  },
  text: {
    margin: 0,
    fontSize: '16px',
    lineHeight: '24px',
    textAlign: 'center',
    marginBottom: '24px'
  },
  input: {
    width: '100%',
    marginBottom: '40px'
  },
  button: {
    width: '100%'
  },
  controls: {
    '& > :not(:last-child)': {
      marginBottom: '8px'
    },
    '& a': {
      textDecoration: 'none'
    },
    '&:not(:last-child)': {
      marginBottom: '30px'
    },
    textAlign: 'center'
  },
  submitAnimated: {
    width: 'initial'
  },
  helpWrapper: {
    textAlign: 'center'
  },
  help: {
    border: 0
  },
  invalid: {
    color: theme.palette.warning.dark
  }
}));

const ZIP_CODE_CHARS_REGEX = /^[0-9 \-A-Za-z]*$/;

const ZipCodeForm = reduxForm({ form: 'zip-code' })(
  ({
    handleSubmit,
    onSubmit,
    invalid,
    pristine,
    onLoginHelpClick,
    isLoading,
    attempt = 0,
    maxAttempts = 1
  }) => {
    const classes = useStyles();
    const invalidZipCode = useMemo(() => attempt > 0, [attempt]);
    const isFailed = useMemo(() => attempt === maxAttempts, [
      attempt,
      maxAttempts
    ]);

    const onZipCodeKeyPress = useCallback(event => {
      const keyCode = event.keyCode || event.which;
      const keyValue = String.fromCharCode(keyCode);

      if (!ZIP_CODE_CHARS_REGEX.test(keyValue)) {
        event.preventDefault();
      }
    }, []);

    return (
      <Form
        className={classes.form}
        onSubmit={handleSubmit(values =>
          onSubmit(values['zip-code'].toUpperCase())
        )}
      >
        <Translation
          className={classNames(
            classes.text,
            invalidZipCode ? classes.invalid : null
          )}
          component="p"
        >
          {attempt === 0
            ? 'ZIP_LOGIN_WELCOME'
            : `ZIP_LOGIN_ATTEMPT${attempt}_ERROR`}
        </Translation>
        {isFailed ? (
          <Translation className={classes.text} component="p">
            ZIP_LOGIN_REGULAR_LOGIN_TEXT
          </Translation>
        ) : (
          <TextInput
            required
            id="zip-code-form-zip-code"
            onKeyPress={onZipCodeKeyPress}
            className={classNames(classes.input)}
            color={invalidZipCode ? 'warningDark' : undefined}
            startIconVariant={invalidZipCode ? 'warningDark' : undefined}
            name="zip-code"
            placeholder="ZIP_LOGIN_FORMHINT"
            startIcon="key"
            validate={required}
          />
        )}
        <div className={classes.controls}>
          {!isFailed ? (
            <ThemeButton
              hasAnimation
              className={classNames(
                classes.button,
                classes.submit,
                isLoading ? classes.submitAnimated : null
              )}
              id="page.home.component.zip-code-form.submit-button"
              disabled={invalid || pristine}
              type="submit"
              variant="contained"
              textKey="Login"
              isLoading={isLoading}
            />
          ) : null}
          <Link
            to={LOGIN}
            id="page.home.component.zip-code-form.regular-login-button"
          >
            <ThemeButton
              className={classNames(classes.button, classes.regularLogin)}
              id="page.home.component.zip-code-form.regular-login-button"
              variant={isFailed ? 'contained' : 'outlined'}
              color="primary"
              textKey={
                isFailed
                  ? 'REGULAR_LOGIN_BUTTON_LABEL'
                  : 'REGULAR_LOGIN_BUTTON_LABEL'
              }
              startIcon={isFailed ? null : 'chevronLeft'}
              startIconColor={isFailed ? null : 'primary'}
              endIcon={!isFailed ? null : 'chevronRight'}
              endIconColor={!isFailed ? null : 'primaryContrast'}
            />
          </Link>
        </div>
        {!isFailed ? (
          <div className={classes.helpWrapper}>
            <ThemeButton
              onClick={onLoginHelpClick}
              className={classes.help}
              iconClassName={classes.helpIcon}
              id="page.home.component.zip-code-form.help-button"
              variant="outlined"
              textKey="ZIP_LOGIN_HELP_BUTTON_LABEL"
              startIcon="questionIcon"
              color="hint"
              startIconColor="hint"
            />
          </div>
        ) : null}
      </Form>
    );
  }
);

export default ZipCodeForm;
